import { ConnectError } from '@bufbuild/connect';
import { yupResolver } from '@hookform/resolvers/yup';
import cn from 'classnames';
import { FC, useEffect, useState } from 'react';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';
import { useNavigate, useSearchParams } from 'react-router';

import { AuthState_AuthStatus } from '@/shared/api/protocol-ts/api/auth/dto_auth_pb';
import {
  MIN_PASSWORD_LENGTH,
  PasswordValidationContext,
  PATHS,
  UserBadRequestError,
} from '@/shared/config';
import { useAppDispatch } from '@/shared/hooks';
import { getSupportEmail, safeJSONParse, trackEvent } from '@/shared/lib';
import {
  Button,
  InlineFormError,
  Input,
  InputPassword,
  Layout,
  Modal,
} from '@/shared/ui/';

import { authLib, authModel } from '@/entities/auth';
import { ModalID, modalModel } from '@/entities/modal';

import {
  MFA_CODE_LENGTH,
  MFACodeField,
  useMFAErrorMessage,
} from '@/features/mfa';

import { CongratsModal } from '@/widgets/CongratsModal';
import { CustomPasswordError } from '@/widgets/CustomPasswordError';
import { ResetPasswordErrorModal } from '@/widgets/ResetPasswordErrorModal';

import {
  NewPasswordFormPayload,
  newPasswordFormSchema,
} from './config/formConfig';
import styles from './NewPassword.module.scss';

type NewPasswordProps = {
  className?: string;
  testID?: string;
};

export const NewPassword: FC<NewPasswordProps> = (props) => {
  const { className, testID } = props;

  const { formatMessage } = useIntl();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [searchParams] = useSearchParams();

  const confirmationToken = searchParams.get('token');

  const [isLinkDead, setIsLinkDead] = useState(false);
  const [isMFARequired, setIsMFARequired] = useState(false);
  const [isRecoveryCode, setIsRecoveryCode] = useState(false);
  const [totpCode, setTOTPCode] = useState('');
  const [recoveryCode, setRecoveryCode] = useState('');
  const [factorError, setFactorError] = useState('');
  const getMFAErrorMessage = useMFAErrorMessage('verify');
  const [passwordMinLength, setPasswordMinLength] =
    useState(MIN_PASSWORD_LENGTH);

  const {
    handleSubmit,
    control,
    formState: { isSubmitting, isSubmitted },
    setValue,
    getFieldState,
    trigger,
  } = useForm<NewPasswordFormPayload, PasswordValidationContext>({
    resolver: yupResolver(newPasswordFormSchema),
    mode: 'onChange',
    context: { passwordMinLength },
  });

  const openNewPasswordCongratsModalHandle = () => {
    dispatch(
      modalModel.actions.openModal({
        modalID: ModalID.CongratsModal,
        data: {
          title: formatMessage({
            id: 'newPassword.congrats.title',
            defaultMessage:
              'Congrats! Your password has been successfully reset.',
          }),

          redirectTo: PATHS.signIn,
        },
      }),
    );
  };

  const onSubmit: SubmitHandler<NewPasswordFormPayload> = async (data) => {
    const { password } = data;

    const factor = isRecoveryCode
      ? { case: 'RecoveryCode' as const, value: recoveryCode.trim() }
      : { case: 'TOTPCode' as const, value: totpCode };

    try {
      if (confirmationToken) {
        const setPasswordResponse = await dispatch(
          authModel.thunks.setPassword({
            Token: confirmationToken,
            Password: password,
            Factor: isMFARequired ? factor : undefined,
          }),
        ).unwrap();

        if (setPasswordResponse.Status === AuthState_AuthStatus.SUCCESSFUL) {
          openNewPasswordCongratsModalHandle();
        }
      }
    } catch (error) {
      // TODO: [1/l]  Investigate reason of this behaviour
      // We need a catch for useForm's isSubmitting to work correctly
      if (authLib.isDeadResetLinkError(error)) {
        setIsLinkDead(true);
        return;
      }

      const { code } = safeJSONParse(ConnectError.from(error).rawMessage);

      switch (code) {
        case UserBadRequestError.MFA_REQUIRED:
          setIsMFARequired(true);
          break;

        case UserBadRequestError.WRONG_CODE:
          setFactorError(
            isRecoveryCode
              ? formatMessage({
                  id: 'newPassword.mfa.wrongRecoveryCode',
                  defaultMessage:
                    'Incorrect recovery code. Check the code and try again.',
                })
              : getMFAErrorMessage(error),
          );
          setTOTPCode('');
          break;

        case UserBadRequestError.CODE_RETRIES_LIMIT:
          setFactorError(
            formatMessage({
              id: 'newPassword.mfa.retriesLimit',
              defaultMessage: 'Too many incorrect codes. Try again later.',
            }),
          );
          setTOTPCode('');
          break;
      }
    }
  };

  const switchFactor = (toRecoveryCode: boolean) => {
    setIsRecoveryCode(toRecoveryCode);
    setTOTPCode('');
    setRecoveryCode('');
    setFactorError('');
  };

  const isFactorReady =
    !isMFARequired ||
    (isRecoveryCode
      ? recoveryCode.trim() !== ''
      : totpCode.length === MFA_CODE_LENGTH);

  useEffect(() => {
    setIsLinkDead(false);

    if (!confirmationToken) {
      return;
    }

    dispatch(authModel.thunks.getPasswordPolicy({ Token: confirmationToken }))
      .unwrap()
      .then(({ MinLength, MFARequired }) => {
        setPasswordMinLength(MinLength);
        setIsMFARequired(MFARequired);
      })
      .catch((error) => {
        if (authLib.isDeadResetLinkError(error)) {
          setIsLinkDead(true);
        }
      });
  }, [confirmationToken]);

  // The modal state is global: close it when the page or the link changes, so it
  // does not reappear on the reset page or over a working link.
  useEffect(() => {
    if (!isLinkDead) {
      return;
    }

    dispatch(
      modalModel.actions.openModal({
        modalID: ModalID.ResetPasswordErrorModal,
        data: { errorType: 'ExpiredTime' },
      }),
    );

    return () => {
      dispatch(modalModel.actions.closeModal(ModalID.ResetPasswordErrorModal));
    };
  }, [isLinkDead]);

  // The new minimum reaches the resolver on this render; re-check what was typed before it arrived.
  useEffect(() => {
    if (getFieldState('password').isDirty) {
      trigger('password');
    }
  }, [passwordMinLength]);

  useEffect(() => {
    trackEvent({
      eventName: 'NewPassword Page Viewed',
      isPageViewEvent: true,
    });
  }, []);

  return (
    <Layout>
      <Layout.Content>
        <Layout.Main>
          <div
            className={cn(styles.container, className)}
            data-testid={testID}
            data-section="new-password"
          >
            <Modal
              data-component="new-password-form"
              title={formatMessage({
                id: 'newPassword.title',
                defaultMessage: 'New password',
              })}
              shouldRenderCloseIconButton={false}
              isOpen={!isLinkDead}
              okButtonText={formatMessage({
                id: 'newPassword.save',
                defaultMessage: 'Save',
              })}
              okButtonProps={{
                type: 'submit',
                form: 'newPasswordForm',
                disabled: !isFactorReady,
                loading: isSubmitting,
              }}
              cancelButtonProps={{ variant: 'tertiary' }}
              onCancel={() => navigate(PATHS.signIn)}
              shouldCloseOnOverlayClick={false}
            >
              <form
                id="newPasswordForm"
                onSubmit={handleSubmit(onSubmit)}
                className={styles.form}
              >
                <Controller
                  control={control}
                  name="password"
                  render={({
                    field: { ref, value, name, onBlur, onChange },
                    fieldState: { error },
                  }) => (
                    <>
                      <InputPassword
                        required
                        ref={ref}
                        value={value}
                        name={name}
                        inputMode="text"
                        type="password"
                        onBlur={onBlur}
                        onChange={onChange}
                        label={formatMessage({
                          id: 'newPassword.newPassword',
                          defaultMessage: 'New password',
                        })}
                        autoComplete="new-password"
                      />
                      {error && (
                        <CustomPasswordError
                          inputValue={value ?? ''}
                          minLength={passwordMinLength}
                        />
                      )}
                      {passwordMinLength < MIN_PASSWORD_LENGTH && (
                        <p className="p3">
                          {formatMessage(
                            {
                              id: 'newPassword.mfaMinimumHint',
                              defaultMessage:
                                'With {count} or more characters you can turn off two-step verification later.',
                            },
                            { count: MIN_PASSWORD_LENGTH },
                          )}
                        </p>
                      )}
                    </>
                  )}
                />

                <Controller
                  control={control}
                  name="confirmPassword"
                  render={({
                    field: { ref, value, name, onBlur, onChange },
                    fieldState: { error, isTouched },
                  }) => (
                    <InputPassword
                      required
                      ref={ref}
                      value={value}
                      name={name}
                      inputMode="text"
                      type="password"
                      onBlur={onBlur}
                      onChange={(event) => {
                        if (!isSubmitted && !isTouched) {
                          setValue(name, event.target.value);
                        } else {
                          onChange(event);
                        }
                      }}
                      label={formatMessage({
                        id: 'newPassword.confirmNewPassword',
                        defaultMessage: 'Confirm new password',
                      })}
                      error={error?.message}
                      autoComplete="new-password"
                    />
                  )}
                />

                {isMFARequired && !isRecoveryCode && (
                  <>
                    <p className="p3">
                      <FormattedMessage
                        id="mfa.verify.description"
                        defaultMessage="Enter the 6-digit code from your authenticator app."
                      />
                    </p>

                    <MFACodeField
                      value={totpCode}
                      disabled={isSubmitting}
                      errorMessage={factorError}
                      onChange={(code) => {
                        setTOTPCode(code);
                        setFactorError('');
                      }}
                      label={
                        <FormattedMessage
                          id="mfa.verify.codeLabel"
                          defaultMessage="Verification code"
                        />
                      }
                    />

                    <Button
                      variant="tertiary"
                      size="medium"
                      disabled={isSubmitting}
                      onClick={() => switchFactor(true)}
                    >
                      <FormattedMessage
                        id="mfa.verify.lostAccess"
                        defaultMessage="Lost access to your authenticator app?"
                      />
                    </Button>
                  </>
                )}

                {isMFARequired && isRecoveryCode && (
                  <>
                    <p className="p3">
                      <FormattedMessage
                        id="newPassword.mfa.recoveryDescription"
                        defaultMessage="Enter one of the ten codes you saved when you set up two-step verification. The code will be used up. Two-step verification stays on."
                      />
                    </p>

                    <Input
                      name="recovery-code"
                      value={recoveryCode}
                      required
                      autoComplete="off"
                      placeholder="xxxx-xxxx"
                      label={formatMessage({
                        id: 'mfa.recovery.codeLabel',
                        defaultMessage: 'Recovery code',
                      })}
                      onChange={(event) => {
                        setRecoveryCode(event.target.value);
                        setFactorError('');
                      }}
                    />

                    {factorError && (
                      <InlineFormError errorMessage={factorError} />
                    )}

                    <p className="p3">
                      <FormattedMessage
                        id="mfa.recovery.noCodesLeft"
                        defaultMessage="No codes left, or lost them too? <link>Contact support</link>"
                        values={{
                          link: (chunks) => (
                            <a href={`mailto:${getSupportEmail()}`}>{chunks}</a>
                          ),
                        }}
                      />
                    </p>

                    <Button
                      variant="tertiary"
                      size="medium"
                      disabled={isSubmitting}
                      onClick={() => switchFactor(false)}
                    >
                      <FormattedMessage
                        id="mfa.recovery.useCodeInstead"
                        defaultMessage="Enter a code instead"
                      />
                    </Button>
                  </>
                )}
              </form>
            </Modal>
          </div>
        </Layout.Main>
      </Layout.Content>

      <CongratsModal />
      <ResetPasswordErrorModal />
    </Layout>
  );
};
